import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

const DISCLAIMER = [
  'Here\u2019s the most up-to-date information we have on baggage fees, provided by Global Distribution System Apollo, and should be used for informational purposes only. Fees may vary based on when and how you pay, frequent flier and military status, baggage size and weight or other factors. Exceptions may apply, so you should use the link below or contact the airline for more information.',
  'Baggage fee estimates are provided for your convenience and are subject to changes by the airlines.',
] as const;

function CloseIcon() {
  return (
    <svg className="booking-baggage-modal__close-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );
}

function CheckedBaggageIcon() {
  return (
    <svg
      className="booking-baggage-modal__title-icon"
      focusable="false"
      viewBox="0 0 36 37"
      aria-hidden="true"
      width={36}
      height={37}
    >
      <g>
        <path
          fill="#c6d9ea"
          d="M31.887 4.32S35 6.037 35.07 8.074c.071 2.04 0 24.854 0 24.854s-1.075 2.726-2.582 2.845c-1.506.12-26.707 0-26.707 0s-3.53.352-3.906-2.216c-.376-2.569 0-10.364 0-10.364V21.37s-.265-2.681 3.15-2.664c3.416.018 15.47 0 15.47 0v-2.46s-1.61-2.401-2.812-2.247c-1.203.154 0-4.28 0-4.28s-.824-5.603 2.812-5.398c3.636.207 11.392 0 11.392 0z"
        />
        <path
          fill="#2e6fa9"
          d="M4.643 34.341c-.795 0-1.442-.7-1.442-1.562v-.742c1.103.197 1.982 1.123 2.2 2.304h-.758zm29.018-1.72c0 .78-.482 1.44-1.14 1.65V11.792h1.14zm0-24.186v.823H18.636v-.823c0-.949.712-1.72 1.588-1.72h11.85c.875 0 1.587.771 1.587 1.72zM29.803 4.18h-7.308V2.988c0-.25.187-.455.419-.455h6.47c.23 0 .419.204.419.454zM18.636 13.44v-1.647h12.482v22.55h-1.193a4.37 4.37 0 0 0 .287-1.563V21.715c0-2.258-1.696-4.096-3.78-4.096h-5.019v-1.192c0-1.648-1.237-2.988-2.758-2.988zm-6.87 2.988c0-.251.188-.454.42-.454h6.47c.23 0 .418.203.418.453v1.193h-7.308v-1.193zm-7.123 3.726H5.4c-.217 1.182-1.096 2.107-2.199 2.304v-.741c0-.862.647-1.563 1.442-1.563zm23.23 2.304c-1.103-.197-1.981-1.122-2.199-2.304h.757c.795 0 1.442.7 1.442 1.563zm-1.442 11.884h-.757c.218-1.181 1.096-2.107 2.2-2.304v.742c0 .861-.647 1.562-1.443 1.562zm-23.23-4.855v-4.478c2.394-.225 4.313-2.274 4.562-4.855h15.548c.25 2.58 2.168 4.63 4.562 4.855v4.478c-2.394.225-4.313 2.275-4.562 4.855H7.763c-.249-2.58-2.168-4.63-4.562-4.855zm-2.338-7.77v11.063c0 2.258 1.696 4.096 3.78 4.096h27.43c2.165 0 3.927-1.909 3.927-4.255V8.436c0-2.321-1.725-4.213-3.859-4.253V2.988C32.141 1.34 30.904 0 29.384 0h-6.47c-1.521 0-2.758 1.34-2.758 2.988v1.194c-2.134.04-3.859 1.932-3.859 4.253v5.004h-4.112c-1.52 0-2.757 1.34-2.757 2.988v1.192H4.643c-2.084 0-3.78 1.838-3.78 4.097z"
        />
      </g>
    </svg>
  );
}

export type BookingBaggageFeesModalProps = {
  open: boolean;
  onClose: () => void;
};

export default function BookingBaggageFeesModal({ open, onClose }: BookingBaggageFeesModalProps) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const content = (
    <div className="booking-baggage-modal-root" role="presentation" onClick={handleBackdrop} onMouseDown={handleBackdrop}>
      <div className="booking-baggage-modal__backdrop" aria-hidden="true" />
      <div className="booking-baggage-modal__container" role="none presentation">
        <div
          className="booking-baggage-modal__paper"
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-baggage-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="booking-baggage-modal__header">
            <div id="booking-baggage-modal-title" className="booking-baggage-modal__title">
              Baggage Fees
            </div>
            <button type="button" className="booking-baggage-modal__close" aria-label="Close" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>

          <div className="booking-baggage-modal__content">
            <div className="booking-baggage-modal__grid">
              <div className="booking-baggage-modal__table-col">
                <h2 className="booking-baggage-modal__section-title">
                  <CheckedBaggageIcon />
                  <span>Checked Baggage</span>
                </h2>
                <table className="booking-baggage-modal__table">
                  <thead>
                    <tr>
                      <th scope="col">Traveler</th>
                      <th scope="col">Allowance and Dimensions</th>
                      <th scope="col">1st</th>
                      <th scope="col">2nd</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Adult</td>
                      <td>
                        <div>DATA NOT AVAILABLE</div>
                      </td>
                      <td>FREE</td>
                      <td>FREE</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="booking-baggage-modal__disclaimers">
                {DISCLAIMER.map((text) => (
                  <span key={text.slice(0, 32)} className="booking-baggage-modal__disclaimer">
                    {text}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
